import { useEffect, useId, useState } from "react";
import {
  Block,
  CheckCircle,
  ErrorOutlineTwoTone,
  HourglassTop,
  MoreVert,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAppDialog } from "@pepsico/layout";
import { noop } from "es-toolkit";

import {
  OrthoApprovalStatus,
  TaskStatus,
  type SiteGeoResponse,
  type TaskResponse,
} from "api/model";
import { useGetSitesGeoByLocation } from "api/sites-geo/sites-geo";
import {
  SELECTED_MAP_ITEM_BGCOLOR,
  SELECTED_MAP_ITEM_BORDER_COLOR,
} from "components/PlotMap/utils/display";
import { usePermissions } from "components/UserProvider";
import { isStitchedOrthoStatus, useCogOrtho } from "context/CogOrtho";
import { loadThumbnailImageUrl } from "context/CogOrtho/cogOrtho";
import {
  canReuploadImages,
  formatOrthoFlightLabel,
  getWorkflowFolderNames,
  getWorkflowUserFolder,
  isSettledUploadStatus,
  isUploadInFlight,
  ORTHO_WORKFLOW_ACTION,
  useOrthoWorkflows,
  type OrthoWorkflowKey,
} from "context/OrthoWorkflow";
import { Permissions } from "defs";

import {
  downloadOrthoRawImages,
  type RawImageDownloadTarget,
} from "./downloadRawImages";

const ORTHO_APPROVAL_ICONS = {
  [OrthoApprovalStatus.approved]: {
    color: "success.dark",
    Icon: CheckCircle,
    label: "Approved",
  },
  [OrthoApprovalStatus.pending]: {
    color: "warning.dark",
    Icon: HourglassTop,
    label: "Pending",
  },
  [OrthoApprovalStatus.rejected]: {
    color: "error.main",
    Icon: Block,
    label: "Rejected",
  },
} as const;

const getApprovalIcon = (status: OrthoApprovalStatus) => {
  switch (status) {
    case OrthoApprovalStatus.processing:
    case OrthoApprovalStatus.uploading:
      return null;
    case OrthoApprovalStatus.approved:
    case OrthoApprovalStatus.pending:
    case OrthoApprovalStatus.rejected:
      return ORTHO_APPROVAL_ICONS[status];
  }
};

interface OrthoFolderItemProps {
  onReupload: (orthoId: number, siteId: number) => void;
  onResumeUpload: (tasks: TaskResponse[]) => void;
  workflowKey: OrthoWorkflowKey;
}

const OrthoFolderItem: React.FC<OrthoFolderItemProps> = ({
  onReupload,
  onResumeUpload,
  workflowKey,
}) => {
  const { displayOrtho, presignedUrls, sessions } = useCogOrtho();
  const permissions = usePermissions();
  const isSuperuser = permissions.includes(Permissions.ADMIN);
  const { setAppDialog } = useAppDialog();
  const menuId = useId();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<{
    completed: number;
    total: number;
  } | null>(null);
  const isMenuOpen = menuAnchorEl != null;
  const {
    actions,
    byKey,
    isActionPending,
    locationUuid,
    upload: { isAnyUploadInProgress },
    workflows,
  } = useOrthoWorkflows();
  const workflow = byKey.get(workflowKey) ?? null;

  const siteId = workflow?.siteId ?? null;
  const { data: sitesGeo = [] } = useGetSitesGeoByLocation(
    { location_uuid: locationUuid },
    {
      query: {
        enabled: locationUuid !== "",
        select: (res): SiteGeoResponse[] =>
          res.status === 200 ? res.data : [],
      },
    },
  );
  const siteLabel =
    siteId == null
      ? null
      : (sitesGeo.find((site) => site.id === siteId)?.site_label ?? null);

  const ortho = workflow?.ortho ?? null;
  const orthoPresignedUrl =
    ortho != null ? (presignedUrls.get(ortho.id) ?? null) : null;
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  useEffect(() => {
    if (orthoPresignedUrl == null) {
      return;
    }
    return loadThumbnailImageUrl(orthoPresignedUrl, setThumbnailUrl);
  }, [orthoPresignedUrl]);

  if (workflow == null) {
    return null;
  }

  const { orthoCreation, stage, upload } = workflow;
  const tasks = upload.batches.flatMap((batch) => batch.tasks);
  const isActivelyUploading = isUploadInFlight(workflow);

  const stitchedOrtho =
    ortho != null && isStitchedOrthoStatus(ortho.status) ? ortho : null;
  const isOrthoOnMap = stitchedOrtho?.isVisible ?? false;
  const orthoSession =
    stitchedOrtho != null ? sessions[stitchedOrtho.id] : undefined;
  const isOrthoLoading =
    isOrthoOnMap &&
    orthoSession?.overlay?.bounds == null &&
    orthoSession?.error == null;
  const cmPerPx = workflow.meta.cmPerPx ?? null;

  const folderNames = getWorkflowFolderNames(workflow);
  const folderLabel = folderNames.length > 0 ? folderNames.join(", ") : null;
  const sharesFlightDate =
    workflow.flightDate != null &&
    workflows.some(
      (other) =>
        other.key !== workflow.key &&
        other.siteId === workflow.siteId &&
        other.flightDate === workflow.flightDate,
    );
  const flightDateLabel =
    workflow.flightDate != null
      ? formatOrthoFlightLabel(
          workflow.flightDate,
          workflow.flightTime,
          sharesFlightDate,
        )
      : null;
  const flightLabel =
    flightDateLabel != null
      ? folderLabel != null
        ? `${flightDateLabel} (${folderLabel})`
        : flightDateLabel
      : (folderLabel ?? "New flight");
  const displayName = `${siteLabel != null ? `${siteLabel} - ` : ""}${flightLabel}`;

  const hasIncompleteTasks = tasks.some(
    (task) => !isSettledUploadStatus(task.status),
  );
  const uploadProgress = workflow.steps.upload.progress;
  const isUploadPaused = !isActivelyUploading && upload.pendingTasks.length > 0;

  const uploadSummary = [
    tasks.length > 0 ? `${upload.uploadedCount} images` : null,
    cmPerPx != null ? `${cmPerPx.toFixed(1)} cm/px` : null,
    isOrthoOnMap && !isOrthoLoading ? "On Map" : null,
  ]
    .filter((part) => part != null)
    .join(" · ");

  const failedOrthoCreationTask =
    orthoCreation.task?.status === TaskStatus.failed
      ? orthoCreation.task
      : null;
  // `errors` is newest first. Upload problems show as the paused chip and the
  // failed-file list (and the session's error in the panel's banner).
  const orthoCreationTaskError = workflow.errors.find(
    (error) => error.source === "task" && error.step === "ortho_creation",
  );
  const clientErrors = workflow.errors.filter(
    (error) => error.source === "client" && error.step !== "upload",
  );

  // Several uploads failed, so creation wasn't started automatically; the user can start it.
  const isAwaitingManualStart =
    stage === "ortho_creation_failed" &&
    orthoCreation.task == null &&
    upload.failedTasks.length > 1;

  const canSelectDisplayedOrtho = stitchedOrtho?.visible_on_map === true;
  const approvalIcon =
    stitchedOrtho != null ? getApprovalIcon(stitchedOrtho.status) : null;
  const ApprovalIcon = approvalIcon?.Icon;
  const hasReviewStatus = approvalIcon != null;
  const showHiddenChip = stitchedOrtho != null && !stitchedOrtho.visible_on_map;
  const showPendingActions =
    ortho?.status === OrthoApprovalStatus.pending && isSuperuser;
  const showCreationFailure =
    !hasReviewStatus &&
    (failedOrthoCreationTask != null || stage === "ortho_creation_failed");
  const creationStatusLabel =
    orthoCreation.task?.status ??
    (ortho?.status === OrthoApprovalStatus.processing
      ? OrthoApprovalStatus.processing
      : null);
  const showCreationStatus =
    !hasReviewStatus && !showCreationFailure && creationStatusLabel != null;
  const canReupload = canReuploadImages(workflow);
  const showNoOrthoYet =
    !hasReviewStatus &&
    !showCreationFailure &&
    creationStatusLabel == null &&
    !canReupload;
  const showStatusRow =
    stage !== "hashing" &&
    workflow.siteId != null &&
    (showHiddenChip ||
      showPendingActions ||
      showCreationFailure ||
      showCreationStatus ||
      showNoOrthoYet ||
      canReupload);

  const handleDisplayOrtho = () => {
    if (ortho == null || !canSelectDisplayedOrtho) return;
    displayOrtho(ortho.id);
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const isReviewPending =
    isActionPending(ORTHO_WORKFLOW_ACTION.approve, workflowKey) ||
    isActionPending(ORTHO_WORKFLOW_ACTION.reject, workflowKey);

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const showDownloadError = (message: string) => {
    setAppDialog({
      confirmText: "Close",
      message: <>{message}</>,
      onConfirm: noop,
      open: true,
      title: "Error",
    });
  };

  const runRawImageDownload = async (
    orthoId: number,
    target: RawImageDownloadTarget,
    descriptionsByHash: ReadonlyMap<string, string>,
  ) => {
    try {
      const result = await downloadOrthoRawImages(orthoId, target, {
        descriptionsByHash,
        onProgress: (completed, total) => {
          setDownloadProgress({ completed, total });
        },
      });
      if (result.kind === "error") {
        showDownloadError(result.message);
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to download images.";
      showDownloadError(message);
    } finally {
      setDownloadProgress(null);
    }
  };

  const handleDownloadImages = (event: React.MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    handleCloseMenu();
    if (ortho == null || downloadProgress != null) {
      return;
    }

    const siteName = siteLabel ?? `Site ${ortho.site_id}`;
    const date = workflow.flightDate ?? "unknown-date";
    const orthoId = ortho.id;
    const target: RawImageDownloadTarget = {
      date,
      siteName,
      userFolder: getWorkflowUserFolder(workflow),
    };
    const descriptionsByHash = new Map<string, string>();
    for (const batch of workflow.upload.batches) {
      for (const task of batch.tasks) {
        const hash = task.file_hash?.trim().toLowerCase() ?? "";
        const description = task.description.trim();
        if (hash === "" || description === "") {
          continue;
        }
        descriptionsByHash.set(hash, description);
      }
    }

    setAppDialog({
      cancelText: "Cancel",
      confirmText: "Download",
      message: (
        <>
          This download might take up a lot of disk space. Are you sure you want
          to proceed?
        </>
      ),
      onCancel: noop,
      onConfirm: () => {
        void runRawImageDownload(orthoId, target, descriptionsByHash);
      },
      open: true,
      title: "Download Images",
    });
  };

  const handleReupload = () => {
    if (ortho == null || siteId == null) return;
    onReupload(ortho.id, siteId);
  };

  const handleToggleVisibleOnMap = (event: React.MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    if (stitchedOrtho == null) return;
    handleCloseMenu();
    actions.setVisibleOnMap(workflowKey, !stitchedOrtho.visible_on_map);
  };

  const withoutPropagation =
    (action: () => void) => (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      action();
    };

  return (
    <Box
      onClick={canSelectDisplayedOrtho ? handleDisplayOrtho : undefined}
      sx={{
        bgcolor: isOrthoOnMap ? SELECTED_MAP_ITEM_BGCOLOR : undefined,
        cursor: canSelectDisplayedOrtho ? "pointer" : undefined,
        p: 1.5,
        border: 0,
        borderBottom: 1,
        borderColor: "divider",
        borderLeft: isOrthoOnMap ? 8 : 0,
        borderLeftColor: isOrthoOnMap
          ? SELECTED_MAP_ITEM_BORDER_COLOR
          : undefined,
        borderStyle: "solid",
        position: "relative",
      }}
    >
      {ortho != null && (
        <>
          <IconButton
            aria-controls={isMenuOpen ? menuId : undefined}
            aria-expanded={isMenuOpen ? "true" : undefined}
            aria-haspopup="menu"
            aria-label="Flight actions"
            onClick={handleOpenMenu}
            size="small"
            sx={{ position: "absolute", right: 4, top: 4 }}
          >
            <MoreVert fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={menuAnchorEl}
            id={menuId}
            onClose={handleCloseMenu}
            open={isMenuOpen}
          >
            {stitchedOrtho != null && (
              <MenuItem
                aria-label={
                  stitchedOrtho.visible_on_map
                    ? "Hide from Map"
                    : "Allow on Map"
                }
                disabled={isActionPending(
                  ORTHO_WORKFLOW_ACTION.setVisibleOnMap,
                  workflowKey,
                )}
                onClick={handleToggleVisibleOnMap}
              >
                {stitchedOrtho.visible_on_map
                  ? "Hide from Map"
                  : "Allow on Map"}
              </MenuItem>
            )}
            <MenuItem
              aria-label="Download Images"
              disabled={downloadProgress != null}
              onClick={handleDownloadImages}
            >
              Download Images
            </MenuItem>
          </Menu>
        </>
      )}
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          gap: 1,
          mb: 0.5,
          pr: ortho != null ? 4 : 0,
        }}
      >
        <Box
          sx={{
            borderRadius: 1,
            flexShrink: 0,
            height: 30,
            width: 40,
            overflow: "hidden",
            position: "relative",
            bgcolor: "grey.400",
            ...(isActivelyUploading && {
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%)",
                backgroundSize: "200% 100%",
                animation: "shimmer 1.4s ease-in-out infinite",
              },
              "@keyframes shimmer": {
                "0%": { backgroundPosition: "-200% 0" },
                "100%": { backgroundPosition: "200% 0" },
              },
            }),
          }}
        >
          {thumbnailUrl != null && (
            <Box
              alt=""
              component="img"
              src={thumbnailUrl}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          )}
        </Box>
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            gap: 0.5,
            minWidth: 0,
          }}
        >
          {approvalIcon != null && ApprovalIcon != null && (
            <Tooltip title={approvalIcon.label}>
              <ApprovalIcon
                aria-hidden={false}
                aria-label={approvalIcon.label}
                fontSize="small"
                role="img"
                sx={{ color: approvalIcon.color }}
              />
            </Tooltip>
          )}
          <Typography noWrap variant="subtitle1">
            {displayName}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          alignItems: "flex-start",
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          mb: 1,
        }}
      >
        {downloadProgress != null && (
          <Typography color="text.secondary" variant="caption">
            {downloadProgress.total === 0
              ? "Preparing image download…"
              : `Downloading images… ${downloadProgress.completed} of ${downloadProgress.total}`}
          </Typography>
        )}
        {isActivelyUploading ? (
          <Typography color="text.secondary" variant="caption">
            {stage === "hashing"
              ? "Preparing images for upload…"
              : `${upload.uploadedCount} of ${upload.totalCount ?? tasks.length} images uploading…`}
          </Typography>
        ) : isUploadPaused ? (
          <Typography color="warning.main" variant="caption">
            {tasks.length - upload.pendingTasks.length} of {tasks.length} images
            uploaded
          </Typography>
        ) : (
          (uploadSummary !== "" || isOrthoLoading) && (
            <Box sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
              <Typography color="text.secondary" variant="caption">
                {uploadSummary}
              </Typography>
              {isOrthoLoading && (
                <CircularProgress
                  aria-label="Loading ortho"
                  color="primary"
                  size={12}
                />
              )}
            </Box>
          )
        )}
        {showStatusRow && (
          <Box sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
            {showHiddenChip && (
              <Chip
                color="info"
                label="Hidden"
                size="small"
                variant="outlined"
              />
            )}
            {showPendingActions && (
              <>
                <Button
                  color="primary"
                  disabled={isReviewPending}
                  onClick={withoutPropagation(() =>
                    actions.approve(workflowKey),
                  )}
                  size="small"
                  variant="contained"
                >
                  Approve
                </Button>
                <Button
                  color="secondary"
                  disabled={isReviewPending}
                  onClick={withoutPropagation(() =>
                    actions.reject(workflowKey),
                  )}
                  size="small"
                  variant="contained"
                >
                  Reject...
                </Button>
              </>
            )}
            {canReupload && (
              <>
                <Chip
                  color="warning"
                  icon={<ErrorOutlineTwoTone />}
                  label="No Images Uploaded"
                  size="small"
                  variant="outlined"
                />
                <Button
                  color="primary"
                  disabled={isAnyUploadInProgress}
                  onClick={withoutPropagation(handleReupload)}
                  size="small"
                  variant="contained"
                >
                  Reupload
                </Button>
              </>
            )}
            {showCreationFailure && (
              <>
                <Chip
                  color={isAwaitingManualStart ? "warning" : "error"}
                  icon={<ErrorOutlineTwoTone />}
                  label={
                    isAwaitingManualStart
                      ? "Ortho Not Created"
                      : "Ortho Creation Failed"
                  }
                  size="small"
                  variant="outlined"
                />
                {orthoCreation.task == null && (
                  <Button
                    color="primary"
                    disabled={isActionPending(
                      ORTHO_WORKFLOW_ACTION.startOrthoCreation,
                      workflowKey,
                    )}
                    onClick={withoutPropagation(() =>
                      actions.startOrthoCreation(workflowKey),
                    )}
                    size="small"
                    variant="outlined"
                  >
                    {isAwaitingManualStart
                      ? `Create Ortho from ${upload.uploadedCount} Images`
                      : "Retry"}
                  </Button>
                )}
                {failedOrthoCreationTask != null && (
                  <Button
                    color="primary"
                    disabled={isActionPending(
                      ORTHO_WORKFLOW_ACTION.retryOrthoCreation,
                      workflowKey,
                    )}
                    onClick={withoutPropagation(() =>
                      actions.retryOrthoCreation(workflowKey),
                    )}
                    size="small"
                    variant="outlined"
                  >
                    Retry
                  </Button>
                )}
              </>
            )}
            {showCreationStatus && (
              <Typography color="text.secondary" variant="caption">
                Ortho Creation Status: {creationStatusLabel}
              </Typography>
            )}
            {showNoOrthoYet && (
              <Typography color="text.secondary" variant="caption">
                No ortho yet
              </Typography>
            )}
          </Box>
        )}
        {orthoCreation.live != null && (
          <Box sx={{ width: "100%" }}>
            <Typography color="text.secondary" variant="caption">
              {orthoCreation.live.stage ?? "Processing"} ·{" "}
              {Math.round(orthoCreation.live.progress)}%
            </Typography>
            <LinearProgress
              sx={{ mt: 0.25 }}
              value={orthoCreation.live.progress}
              variant="determinate"
            />
          </Box>
        )}
        {failedOrthoCreationTask != null && orthoCreationTaskError != null && (
          <Typography color="error" variant="caption">
            Reason: {orthoCreationTaskError.message}
          </Typography>
        )}
      </Box>
      {(isActivelyUploading || hasIncompleteTasks) && (
        <LinearProgress
          sx={{ mb: 1.5 }}
          value={uploadProgress ?? 0}
          variant={
            isActivelyUploading && uploadProgress == null
              ? "indeterminate"
              : "determinate"
          }
          color={isActivelyUploading ? "primary" : "error"}
        />
      )}
      {isUploadPaused && (
        <Box sx={{ mb: 1.5, gap: 0.5, alignItems: "center", display: "flex" }}>
          <Chip
            icon={<ErrorOutlineTwoTone />}
            label="Upload Paused"
            variant="outlined"
            color="error"
          />
          <Button
            aria-label={`Resume uploads for ${folderLabel ?? flightLabel}`}
            disabled={isAnyUploadInProgress}
            onClick={(event) => {
              event.stopPropagation();
              onResumeUpload(tasks);
            }}
            size="small"
            variant="contained"
          >
            Resume Upload
          </Button>
        </Box>
      )}
      {clientErrors.map((error) => (
        <Typography
          key={`${error.step}:${error.at}`}
          color="error"
          component="p"
          variant="caption"
        >
          {error.message}
        </Typography>
      ))}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {upload.failedTasks.map((task) => (
          <Box
            key={task.id}
            sx={{
              alignItems: "center",
              display: "flex",
              gap: 1,
              justifyContent: "space-between",
            }}
          >
            <Typography noWrap sx={{ minWidth: 0 }} variant="body2">
              {task.description.split("/").pop()}
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ flexShrink: 0 }}
              variant="caption"
            >
              {task.status}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default OrthoFolderItem;
